import { createI18n } from 'vue-i18n'

import {
	en as UiKitEnLocales,
	fa as UiKitFaLocales
} from '@barbanet/ui-kit/locales'

import router from '../router'

const supportedLocales = ['en', 'fa']

const defaultLocale = 'fa'

// ——————————————————————————————————————————
// Dynamically import all shared locale files (e.g., ../locales/fa/common.ts)
// ——————————————————————————————————————————
const sharedImporters = import.meta.glob<{ default: Record<string, unknown> }>(
	'../locales/*/*.ts'
)

const loadedSharedLocales = new Set<string>()

/**
 * Load shared translation files for a specific locale (e.g., 'common', 'auth').
 * Skips already loaded locales to avoid redundant imports.
 */
export async function loadShared(locale: string) {
	if (loadedSharedLocales.has(locale)) {
		return
	}

	const msgs: Record<string, Record<string, unknown>> = {}

	for (const [path, loader] of Object.entries(sharedImporters)) {
		const match = path.match(/locales\/([^/]+)\/([^/]+)\.ts$/)
		if (!match || match[1] !== locale) {
			continue
		}
		const namespace = match[2]
		const mod = await loader()

		msgs[namespace] = mod.default
	}

	i18n.global.mergeLocaleMessage(locale, msgs)
	i18n.global.mergeLocaleMessage(locale, {
		...(locale === 'en' ? UiKitEnLocales : UiKitFaLocales)
	})
	loadedSharedLocales.add(locale)
}

// ——————————————————————————————————————————
// Dynamically import all domain-specific locale files
// Only includes .ts files that match supported locales
// ——————————————————————————————————————————
const domainImporters = import.meta.glob<{ default: Record<string, unknown> }>(
	'../domains/*/locales/*.ts'
)
const domainLocaleMap: Record<
	string,
	Record<string, Array<() => Promise<{ default: Record<string, unknown> }>>>
> = {}
const loadedDomainLocales = new Set<string>()

// Build a map of domain -> locale -> loaders (only for supported locales)
for (const [path, loader] of Object.entries(domainImporters)) {
	const match = path.match(/domains\/([^/]+)\/locales\/([^/]+)\.ts$/)
	if (!match) {
		continue
	}
	const [, domain, locale] = match
	if (!supportedLocales.includes(locale)) {
		continue
	}
	domainLocaleMap[domain] ??= {}
	domainLocaleMap[domain][locale] ??= []
	domainLocaleMap[domain][locale].push(loader)
}

/**
 * Load translation files for a given domain and locale.
 * Skips if already loaded. Files are merged under the domain key.
 */
export async function loadDomainMessages(
	domain: string,
	locale: string = i18n.global.locale.value
) {
	const key = `${domain}-${locale}`

	if (loadedDomainLocales.has(key)) {
		return
	}

	const importers = domainLocaleMap[domain]?.[locale] || []
	for (const loader of importers) {
		const mod = await loader()
		i18n.global.mergeLocaleMessage(locale, { [domain]: mod.default })
	}

	loadedDomainLocales.add(key)
}

// ——————————————————————————————————————————
// Create and configure the i18n instance
// Starts with empty messages, uses lazy loading
// ——————————————————————————————————————————
export const i18n = createI18n({
	legacy: false,
	locale: defaultLocale,
	fallbackLocale: 'en',
	messages: {}
})

/**
 * Sets the active locale and loads both shared and domain-specific messages.
 */
export async function setLocale(locale: string) {
	// Load shared messages first
	await loadShared(locale)

	// Update current i18n locale
	i18n.global.locale.value = locale
	document.documentElement.setAttribute('dir', locale === 'fa' ? 'rtl' : 'lrt')

	// Load domain-specific messages based on the current route
	const domain = router?.currentRoute?.value?.meta?.domain as string | undefined
	if (domain) {
		await loadDomainMessages(domain, locale)
	}
}

setLocale(defaultLocale)
